import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Gamepad2, Home, RotateCcw, Trophy, WifiOff, Users, Bot,
} from 'lucide-react';

// ============================================================
// टिक ट्याक टो — /game
//  • नाम हाल्ने → X वा O रोज्ने → कम्प्युटरसँग खेल्ने
//  • जित/हार/बराबरी स्ट्याट (localStorage मा प्रति-खेलाडी save)
//  • 100% offline — इन्टरनेट बिना पनि पूर्ण चल्छ
// ============================================================

const LINES = [
  [0, 1, 2], [3, 4, 5], [6, 7, 8],
  [0, 3, 6], [1, 4, 7], [2, 5, 8],
  [0, 4, 8], [2, 4, 6],
];

function winnerOf(b) {
  for (const line of LINES) {
    const [a, c, d] = line;
    if (b[a] && b[a] === b[c] && b[a] === b[d]) return { sym: b[a], line };
  }
  return b.every(Boolean) ? { sym: 'draw', line: [] } : null;
}

// Computer AI — smart but beatable (85% optimal, कहिलेकाहीँ random)
function computerMove(board, ai, human) {
  const empties = board.map((v, i) => (v ? -1 : i)).filter((i) => i >= 0);
  if (empties.length === 0) return null;

  const findLine = (sym) => {
    for (const i of empties) {
      const copy = [...board];
      copy[i] = sym;
      const w = winnerOf(copy);
      if (w && w.sym === sym) return i;
    }
    return -1;
  };

  if (Math.random() < 0.85) { const m = findLine(ai); if (m >= 0) return m; }
  if (Math.random() < 0.85) { const m = findLine(human); if (m >= 0) return m; }
  if (!board[4] && Math.random() < 0.8) return 4;

  const corners = [0, 2, 6, 8].filter((i) => !board[i]);
  if (corners.length && Math.random() < 0.7) {
    return corners[Math.floor(Math.random() * corners.length)];
  }
  return empties[Math.floor(Math.random() * empties.length)];
}

const EMPTY_STATS = { wins: 0, losses: 0, draws: 0, recent: [] };
const statsKey = (name) => `tictactoe_stats_${String(name).toLowerCase()}`;

function loadStats(name) {
  try {
    const raw = localStorage.getItem(statsKey(name));
    return raw ? { ...EMPTY_STATS, ...JSON.parse(raw) } : { ...EMPTY_STATS };
  } catch {
    return { ...EMPTY_STATS };
  }
}

function saveStats(name, s) {
  try {
    localStorage.setItem(statsKey(name), JSON.stringify(s));
  } catch { /* storage full — ignore */ }
}

const SYM_CLS = {
  X: 'text-crimson',
  O: 'text-blue',
};

function GamePage() {
  const [screen, setScreen] = useState('setup'); // setup | play
  const [name, setName] = useState('');
  const [playerSym, setPlayerSym] = useState('X');
  const [board, setBoard] = useState(Array(9).fill(null));
  const [turn, setTurn] = useState('player');
  const [result, setResult] = useState(null); // 'win' | 'loss' | 'draw' | null
  const [stats, setStats] = useState(EMPTY_STATS);
  const finishedRef = useRef(false);

  const cpuSym = playerSym === 'X' ? 'O' : 'X';
  const cpuName = 'कम्प्युटर';

  // Game end detection + CPU move
  useEffect(() => {
    if (screen !== 'play' || result) return undefined;

    const w = winnerOf(board);
    if (w) {
      finishedRef.current = true;
      const outcome = w.sym === 'draw' ? 'draw' : w.sym === playerSym ? 'win' : 'loss';
      setResult(outcome);
      setStats((prev) => {
        const next = {
          ...prev,
          wins: prev.wins + (outcome === 'win' ? 1 : 0),
          losses: prev.losses + (outcome === 'loss' ? 1 : 0),
          draws: prev.draws + (outcome === 'draw' ? 1 : 0),
          recent: [...prev.recent, outcome[0].toUpperCase()].slice(-6),
        };
        saveStats(name, next);
        return next;
      });
      return undefined;
    }

    if (turn === 'cpu') {
      const t = setTimeout(() => {
        setBoard((b) => {
          if (winnerOf(b)) return b;
          const m = computerMove(b, cpuSym, playerSym);
          if (m == null || b[m]) return b;
          const nb = [...b];
          nb[m] = cpuSym;
          return nb;
        });
        setTurn('player');
      }, 550);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [board, turn, screen, result, playerSym, cpuSym, name]);

  const startGame = () => {
    if (!name.trim()) return;
    setStats(loadStats(name.trim()));
    newRound();
    setScreen('play');
  };

  const newRound = () => {
    finishedRef.current = false;
    setResult(null);
    setBoard(Array(9).fill(null));
    // X sadhai pahile khelchha — player X bhaye player ko turn suru
    setTurn(playerSym === 'X' ? 'player' : 'cpu');
  };

  const handleCell = (i) => {
    if (board[i] || result || turn !== 'player') return;
    const nb = [...board];
    nb[i] = playerSym;
    setBoard(nb);
    setTurn('cpu');
  };

  const totalGames = stats.wins + stats.losses + stats.draws;
  const winRate = totalGames ? Math.round((stats.wins / totalGames) * 100) : 0;

  const recentChipCls = { W: 'bg-green-100 text-green-700', L: 'bg-red-100 text-red-600', D: 'bg-gray-100 text-gray-600' };

  return (
    <main className="page max-w-[560px] mx-auto px-5 pt-7 pb-[60px] w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2.5">
          <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-800 text-white flex items-center justify-center shadow-lg shadow-emerald-700/25">
            <Gamepad2 size={19} />
          </span>
          <div>
            <h1 className="font-display text-[21px] font-bold leading-none m-0">टिक ट्याक टो</h1>
            <p className="text-[11px] text-muted m-0 mt-1 inline-flex items-center gap-1">
              <WifiOff size={10} /> इन्टरनेट बिना पनि चल्ने खेल
            </p>
          </div>
        </div>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-[12.5px] font-bold text-muted hover:text-crimson transition-colors"
        >
          <Home size={14} /> गृहपृष्ठ
        </Link>
      </div>

      {screen === 'setup' && (
        /* ---------- SETUP: नाम + X/O choice ---------- */
        <div className="bg-white border border-line rounded-2xl shadow-sm p-6 sm:p-7">
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink-soft mb-5">
            तपाईंको नाम
            <input
              type="text"
              value={name}
              maxLength={20}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && name.trim() && startGame()}
              placeholder="आफ्नो नाम लेख्नुहोस्…"
              autoFocus
              className="border border-line rounded-xl px-3.5 py-2.5 text-sm outline-none bg-paper-soft focus:border-crimson focus:bg-white transition-colors"
            />
          </label>

          <p className="text-[13px] font-semibold text-ink-soft mb-2.5">संकेत रोज्नुहोस्</p>
          <div className="grid grid-cols-2 gap-3 mb-6">
            {['X', 'O'].map((sym) => (
              <button
                key={sym}
                type="button"
                onClick={() => setPlayerSym(sym)}
                className={`py-5 rounded-2xl border-2 font-display text-[34px] font-black transition-all ${
                  playerSym === sym
                    ? 'border-current scale-[1.03] shadow-lg bg-white'
                    : 'border-line text-muted hover:border-emerald-300 hover:text-ink'
                } ${SYM_CLS[sym]}`}
              >
                {sym}
                <span className="block font-body text-[11px] font-bold text-muted tracking-wide mt-1">
                  {sym === playerSym ? 'छानिएको' : ''}
                </span>
              </button>
            ))}
          </div>

          <button
            type="button"
            disabled={!name.trim()}
            onClick={startGame}
            className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 to-emerald-800 text-white font-bold text-[14px] px-5 py-3 rounded-xl shadow-md shadow-emerald-700/25 hover:shadow-lg hover:-translate-y-px disabled:opacity-40 disabled:hover:translate-y-0 transition-all"
          >
            <Gamepad2 size={16} /> खेल सुरु गर्नुहोस्
          </button>
        </div>
      )}

      {screen === 'play' && (
        <>
          {/* Scoreboard */}
          <div className="grid grid-cols-3 gap-2.5 mb-4">
            <div className="bg-white border border-line rounded-xl p-3 text-center">
              <p className={`font-display text-[22px] font-black leading-none ${SYM_CLS[playerSym]}`}>{playerSym}</p>
              <p className="text-[11px] font-semibold text-muted truncate mt-1">{name}</p>
              <p className="text-[15px] font-extrabold text-green-600 leading-none mt-1.5">{stats.wins}</p>
              <p className="text-[9.5px] text-muted uppercase tracking-wider">जित</p>
            </div>
            <div className="bg-paper-soft border border-line rounded-xl p-3 text-center">
              <p className="font-display text-[22px] font-black leading-none text-muted">=</p>
              <p className="text-[11px] font-semibold text-muted mt-1">बराबरी</p>
              <p className="text-[15px] font-extrabold text-ink-soft leading-none mt-1.5">{stats.draws}</p>
              <p className="text-[9.5px] text-muted uppercase tracking-wider">&nbsp;</p>
            </div>
            <div className="bg-white border border-line rounded-xl p-3 text-center">
              <p className={`font-display text-[22px] font-black leading-none ${SYM_CLS[cpuSym]}`}>{cpuSym}</p>
              <p className="text-[11px] font-semibold text-muted truncate mt-1">{cpuName}</p>
              <p className="text-[15px] font-extrabold text-red-500 leading-none mt-1.5">{stats.losses}</p>
              <p className="text-[9.5px] text-muted uppercase tracking-wider">जित</p>
            </div>
          </div>

          {/* Status */}
          <div className="text-center mb-3 h-7">
            {result ? (
              <p className={`m-0 text-[15px] font-extrabold ${result === 'win' ? 'text-green-600' : result === 'loss' ? 'text-crimson' : 'text-ink-soft'}`}>
                {result === 'win' ? '🎉 बधाईछ! तपाईं जित्नुभयो!' : result === 'loss' ? `${cpuName} ले जित्यो — फेरि प्रयास गर्नुहोस्!` : 'खेल बराबरीमा सकियो।'}
              </p>
            ) : (
              <p className="m-0 text-[13px] font-semibold text-muted">
                {turn === 'player' ? `तपाईंको पालो (${playerSym})` : `${cpuName} सोच्दैछ… (${cpuSym})`}
              </p>
            )}
          </div>

          {/* Board */}
          <div className="relative bg-white border border-line rounded-2xl shadow-sm p-4 mb-4">
            <div className="grid grid-cols-3 gap-2 max-w-[320px] mx-auto select-none">
              {board.map((cell, i) => {
                const isWinLine = result && result !== 'draw' && winnerOf(board)?.line?.includes(i);
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleCell(i)}
                    disabled={!!cell || !!result || turn !== 'player'}
                    aria-label={`cell ${i + 1}`}
                    className={`aspect-square rounded-xl border text-[40px] font-black flex items-center justify-center transition-all duration-150 ${
                      isWinLine
                        ? 'bg-emerald-50 border-emerald-400 scale-[1.04]'
                        : 'border-line bg-paper-soft hover:border-emerald-300 hover:bg-white active:scale-95'
                    } ${cell ? SYM_CLS[cell] : ''} ${!cell && !result && turn === 'player' ? 'cursor-pointer' : 'cursor-default'}`}
                  >
                    {cell}
                  </button>
                );
              })}
            </div>

            {/* Result overlay actions */}
            {result && (
              <div className="flex items-center justify-center gap-2.5 mt-4">
                <button
                  type="button"
                  onClick={newRound}
                  className="inline-flex items-center gap-1.5 bg-gradient-to-r from-emerald-600 to-emerald-800 text-white font-bold text-[12.5px] px-5 py-2.5 rounded-full shadow-md hover:shadow-lg transition-all"
                >
                  <RotateCcw size={13} /> फेरि खेल्नुहोस्
                </button>
                <button
                  type="button"
                  onClick={() => setScreen('setup')}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-line text-[12.5px] font-bold text-ink-soft hover:border-crimson hover:text-crimson transition-colors"
                >
                  <Users size={13} /> नयाँ खेलाडी
                </button>
              </div>
            )}
          </div>

          {/* Stats panel */}
          <div className="bg-white border border-line rounded-2xl p-4 sm:p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-[14.5px] font-bold m-0 inline-flex items-center gap-1.5">
                <Trophy size={15} className="text-gold" /> {name.trim() || 'खेलाडी'} को रेकर्ड
              </h3>
              <span className="text-[11px] font-bold text-muted">{totalGames} खेल • जितदर {winRate}%</span>
            </div>

            {/* Win rate bar */}
            <div className="h-2 rounded-full bg-paper-tint overflow-hidden mb-3">
              <div className="h-full rounded-full bg-gradient-to-r from-green-500 to-emerald-600 transition-all duration-500" style={{ width: `${winRate}%` }} />
            </div>

            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex gap-1.5">
                {stats.recent.length === 0 ? (
                  <span className="text-[11.5px] text-muted">अझै कुनै खेल खेलिएको छैन</span>
                ) : (
                  stats.recent.map((r, i) => (
                    <span key={`${i}-${r}`} className={`w-6 h-6 rounded-md text-[11px] font-black flex items-center justify-center ${recentChipCls[r]}`}>
                      {r}
                    </span>
                  ))
                )}
              </div>
              <span className="text-[11px] text-muted inline-flex items-center gap-1">
                <Bot size={11} /> vs {cpuName}
              </span>
            </div>
          </div>
        </>
      )}
    </main>
  );
}

export default GamePage;
